import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, User, Lock, Mail, Shield, Calendar, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const Account = () => {
  const { user, loading, updateProfile, updatePassword, updateEmail, getMFAStatus } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Profile state
  const [fullName, setFullName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Email state
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showMfaDialog, setShowMfaDialog] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showDisableMfaDialog, setShowDisableMfaDialog] = useState(false);
  const [disableMfaPassword, setDisableMfaPassword] = useState('');

  // Account info
  const [accountCreated, setAccountCreated] = useState<string>('');
  const [emailVerified, setEmailVerified] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      loadUserProfile();
      loadMFAStatus();
      setEmailVerified(!!user.email_confirmed_at);
      setAccountCreated(new Date(user.created_at).toLocaleDateString());
    }
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', user.id)
      .single();

    if (!error && data) {
      setFullName(data.full_name || '');
    }
  };

  const loadMFAStatus = async () => {
    const { enabled } = await getMFAStatus();
    setMfaEnabled(enabled);
  };

  const handleUpdateProfile = async () => {
    if (!fullName.trim()) {
      toast({
        title: "Error",
        description: "Name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setProfileLoading(true);
    const { error } = await updateProfile(fullName);
    setProfileLoading(false);

    if (!error) {
      setIsEditingName(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "Error",
        description: "All password fields are required",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Error",
        description: "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    // Password strength validation
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumber) {
      toast({
        title: "Error",
        description: "Password must contain uppercase, lowercase, and number",
        variant: "destructive",
      });
      return;
    }

    setPasswordLoading(true);
    await updatePassword(currentPassword, newPassword);
    setPasswordLoading(false);

    // Clear fields
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !emailPassword) {
      toast({
        title: "Error",
        description: "Both fields are required",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast({
        title: "Error",
        description: "Invalid email format",
        variant: "destructive",
      });
      return;
    }

    setEmailLoading(true);
    const { error } = await updateEmail(newEmail, emailPassword);
    setEmailLoading(false);

    if (!error) {
      setNewEmail('');
      setEmailPassword('');
    }
  };

  const handleEnableMFA = async () => {
    setMfaLoading(true);
    
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });

      setMfaLoading(false);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to enable MFA",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setMfaQrCode(data.totp.qr_code);
        setMfaSecret(data.totp.secret);
        setMfaFactorId(data.id);
        setShowMfaDialog(true);
      }
    } catch (error) {
      setMfaLoading(false);
      toast({
        title: "Error",
        description: "Failed to enable MFA",
        variant: "destructive",
      });
    }
  };

  const handleVerifyMFA = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      toast({
        title: "Error",
        description: "Please enter a valid 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setMfaLoading(true);
    
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: mfaFactorId,
        code: mfaCode,
      });

      if (error) {
        setMfaLoading(false);
        toast({
          title: "Error",
          description: "Invalid verification code",
          variant: "destructive",
        });
        return;
      }

      // Update MFA status in database
      await supabase
        .from('user_mfa_status')
        .upsert({
          user_id: user!.id,
          mfa_required: true,
          mfa_completed: true,
          completed_at: new Date().toISOString(),
        });

      setMfaLoading(false);
      toast({
        title: "Success",
        description: "MFA enabled successfully",
      });
      setShowMfaDialog(false);
      setMfaCode('');
      setMfaEnabled(true);
    } catch (error) {
      setMfaLoading(false);
      toast({
        title: "Error",
        description: "Failed to verify MFA",
        variant: "destructive",
      });
    }
  };

  const handleDisableMFA = async () => {
    if (!disableMfaPassword) {
      toast({
        title: "Error",
        description: "Password is required",
        variant: "destructive",
      });
      return;
    }

    setMfaLoading(true);

    try {
      // Re-authenticate
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user!.email!,
        password: disableMfaPassword,
      });

      if (reAuthError) {
        setMfaLoading(false);
        toast({
          title: "Error",
          description: "Password is incorrect",
          variant: "destructive",
        });
        return;
      }

      // Get all MFA factors
      const { data: factors } = await supabase.auth.mfa.listFactors();
      
      // Unenroll all factors
      if (factors?.totp) {
        for (const factor of factors.totp) {
          await supabase.auth.mfa.unenroll({ factorId: factor.id });
        }
      }

      // Update MFA status in database
      await supabase
        .from('user_mfa_status')
        .update({
          mfa_required: false,
          mfa_completed: false,
          completed_at: null,
        })
        .eq('user_id', user!.id);

      setMfaLoading(false);
      toast({
        title: "Success",
        description: "MFA disabled successfully",
      });
      setShowDisableMfaDialog(false);
      setDisableMfaPassword('');
      setMfaEnabled(false);
    } catch (error) {
      setMfaLoading(false);
      toast({
        title: "Error",
        description: "Failed to disable MFA",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Account Settings - 4FOX Security</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="container mx-auto px-4 pt-24 pb-16 bg-secondary/30">
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Account Settings</h1>
              <p className="text-muted-foreground">Manage your profile and security preferences</p>
            </div>

            {/* Profile Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profile Information
                </CardTitle>
                <CardDescription>Your personal information and account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="bg-muted"
                    />
                    {emailVerified && (
                      <Badge variant="outline" className="gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <div className="flex gap-2">
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      disabled={!isEditingName}
                      className={!isEditingName ? 'bg-muted' : ''}
                    />
                    {isEditingName ? (
                      <>
                        <Button
                          onClick={handleUpdateProfile}
                          disabled={profileLoading}
                        >
                          {profileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditingName(false);
                            loadUserProfile();
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => setIsEditingName(true)}>Edit</Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="space-y-1">
                    <Label className="text-sm text-muted-foreground">Account Created</Label>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4" />
                      {accountCreated}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your password to keep your account secure</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You will be logged out after changing your password and will need to log in again.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <Button
                  onClick={handleUpdatePassword}
                  disabled={passwordLoading}
                  className="w-full md:w-auto"
                >
                  {passwordLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Change Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Change Email Address
                </CardTitle>
                <CardDescription>Update your email address (requires verification)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    A verification email will be sent to your new address. You must confirm it before the change takes effect.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="newEmail">New Email Address</Label>
                  <Input
                    id="newEmail"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="newemail@4fox.uk"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="emailPassword">Current Password</Label>
                  <Input
                    id="emailPassword"
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                  />
                </div>

                <Button
                  onClick={handleUpdateEmail}
                  disabled={emailLoading}
                  className="w-full md:w-auto"
                >
                  {emailLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Email'
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Multi-Factor Authentication */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Multi-Factor Authentication
                </CardTitle>
                <CardDescription>Add an extra layer of security to your account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">MFA Status</p>
                      {mfaEnabled ? (
                        <Badge className="gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Enabled
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <XCircle className="w-3 h-3" />
                          Disabled
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {mfaEnabled
                        ? 'Your account is protected with two-factor authentication'
                        : 'Enhance your account security by enabling MFA'}
                    </p>
                  </div>
                  {mfaEnabled ? (
                    <Button
                      variant="destructive"
                      onClick={() => setShowDisableMfaDialog(true)}
                      disabled={mfaLoading}
                    >
                      Disable MFA
                    </Button>
                  ) : (
                    <Button
                      onClick={handleEnableMFA}
                      disabled={mfaLoading}
                    >
                      {mfaLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Enable MFA'
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>

      {/* MFA Setup Dialog */}
      <Dialog open={showMfaDialog} onOpenChange={setShowMfaDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Multi-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {mfaQrCode && (
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={mfaQrCode} alt="MFA QR Code" className="w-48 h-48" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Manual Entry Code</Label>
              <Input value={mfaSecret} readOnly className="font-mono text-xs" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfaCode">Verification Code</Label>
              <Input
                id="mfaCode"
                type="text"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-lg tracking-widest"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowMfaDialog(false);
                setMfaCode('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleVerifyMFA} disabled={mfaLoading}>
              {mfaLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                'Verify & Enable'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable MFA Dialog */}
      <Dialog open={showDisableMfaDialog} onOpenChange={setShowDisableMfaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password to confirm you want to disable MFA. This will make your account less secure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disableMfaPassword">Password</Label>
              <Input
                id="disableMfaPassword"
                type="password"
                value={disableMfaPassword}
                onChange={(e) => setDisableMfaPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDisableMfaDialog(false);
                setDisableMfaPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisableMFA}
              disabled={mfaLoading}
            >
              {mfaLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Account;
